import { Link } from 'react-router-dom';
import { useState, useContext, useEffect } from 'react';
import Select from 'react-select';
import { AppContext } from '../util/AppContext';
import { getConfigValue, isBatchPrintEnabledForAnyProduct } from '../util/getConfigValue';
import CreateSalesAidStepProgressBar, { SalesAidStepIds } from '../components/CreateSalesAidStepProgressBar.js';
import SelectTargetComponent from '../components/SelectTargetComponent.js';
import { useRoutingValidation } from '../util/useRoutingValidation.js';
import { useBreadcrumbs } from '../util/BreadcrumbContext.js';
import { getPrescribersByTerritory, getPrescriberById } from '../API/prescriber.js';
import { getGroupPracticesByTerritory, getGroupPracticeById } from '../API/groupPractice.js';
import { getGeographyAggregatesByTerritory, getGeographyAggregateById } from '../API/geographyAggregate.js';
import { filterByValue } from '../util/filterByValue.js';
import { hcpType, hcpTab, quickSheetTypeMessageId, focusTypeName } from '../util/constants.js';
import { useErrorBoundary } from 'react-error-boundary';
import { getQuickSheets } from '../API/quickSheets';
import { getMarketsForSellSheetGeneration } from '../util/getMarkets.js';
import { ProductsModal, isProductEnabledForCurrentTerritory } from '../components/ProductsModal.js';

const HCPPage = () => {
	const [appState, setAppState] = useContext(AppContext);
	const [searchInput, setSearchInput] = useState('');
	const [activeId, setActiveId] = useState(null);
	const [showResults, setShowResults] = useState(true);
	const [marketList, setMarketList] = useState([]);
	const [territoryList, setTerritoryList] = useState([]);
	const [selectedMarket, setSelectedMarket] = useState(appState.market || null);
	const [selectedTerritory, setSelectedTerritory] = useState(appState.territory || null);
	const [showProductsModal, setShowProductsModal] = useState(false);

	const topButtonsEnabled = getConfigValue(appState.config.Target.TopButtons?.Enabled, appState)?.Value === 'true';
	const hcpOpportunityEnabled = getConfigValue(appState.config?.HCP?.Opportunity?.Enabled, appState)?.Value === 'true';
	const batchPrintEnabled = isBatchPrintEnabledForAnyProduct(appState);
	const useStickyNavigation = getConfigValue(appState.config.CreateSalesAid?.Features?.StickyNavigation?.Enabled, appState)?.Value === 'true';
	const selectMarketConfigValue = getConfigValue(appState.config?.Territory?.SelectMarket, appState)?.Value || 'Market';
	const selectTerritoryConfigValue = getConfigValue(appState.config?.Territory?.SelectTerritory, appState)?.Value || 'Territory';
	const productLabel = getConfigValue(appState.config?.PlanList?.ProductLabel, appState)?.Value || 'Brand';
	const currentProductName = appState?.currentProduct?.name || appState?.currentProduct?.productName || 'Select Brand';
	const selectorFontSize = '0.82rem';
	const removeIndicatorSeparator = () => null;
	const { setBreadcrumbs } = useBreadcrumbs();
	useEffect(() => {
		setBreadcrumbs([{ label: getConfigValue(appState.config?.Focus?.Header, appState)?.Value, path: '/FocusPage' }]);
	}, [setBreadcrumbs]);

	useRoutingValidation();

	const { showBoundary } = useErrorBoundary();
	
	// Set default focus to HCPs if not already set
	useEffect(() => {
		if (!appState.focus) {
			setAppState((prev) => ({
				...prev,
				focus: focusTypeName.HCPs,
			}));
		}
	}, [appState.focus, setAppState]);
	
	useEffect(() => {
		if (appState.prescriber) {
			setActiveId(appState.prescriber.id);
		}
	}, [appState.prescriber, setActiveId]);

	useEffect(() => {
		const marketsFromSession = JSON.parse(sessionStorage.getItem('marketsForUser')) || [];
		const products = appState?.sellSheetProductDetails || [];
		if (products.length === 0 || marketsFromSession.length === 0) {
			setMarketList([]);
			setTerritoryList([]);
			return;
		}

		const filteredMarkets = getMarketsForSellSheetGeneration(products, marketsFromSession);
		setMarketList(filteredMarkets);
	}, [appState?.sellSheetProductDetails]);

	useEffect(() => {
		if (!marketList.length) {
			return;
		}

		const resolvedMarket = marketList.find((market) => market.id === appState?.market?.id) || marketList[0];
		const availableTerritories = resolvedMarket?.territories || [];
		const resolvedTerritory =
			availableTerritories.find((territory) => territory.id === appState?.territory?.id) || availableTerritories[0] || null;

		setSelectedMarket(resolvedMarket);
		setTerritoryList(availableTerritories);
		setSelectedTerritory(resolvedTerritory);

		if (resolvedMarket?.id !== appState?.market?.id || resolvedTerritory?.id !== appState?.territory?.id) {
			setAppState((prevState) => ({
				...prevState,
				market: resolvedMarket,
				territory: resolvedTerritory,
			}));
		}
	}, [marketList, appState?.market?.id, appState?.territory?.id, setAppState]);

	useEffect(() => {
		if(!hcpOpportunityEnabled){
			// If HCP Opportunity is not enabled, the product is not selected yet and should not be included in API calls.  
			// Clear it out if it exists.
			setAppState((prevState) => ({
				...prevState,
				currentProduct: '',
			}));
			return;
		}
		const products = appState?.sellSheetProductDetails || [];
		if (!products.length) {
			return;
		}

		const availableProducts = products.filter((product) =>
			isProductEnabledForCurrentTerritory(appState?.territory, product.clientProductId)
		);
		const firstProduct = availableProducts[0] || products[0];
		const currentProductId = appState?.currentProduct?.clientProductId;

		const isCurrentProductValid =
			currentProductId !== null &&
			availableProducts.some(
				(product) =>
					product.clientProductId === currentProductId
			);


		if (firstProduct && !isCurrentProductValid) {
			setAppState((prevState) => ({
				...prevState,
				currentProduct: firstProduct,
			}));
		}
	}, [hcpOpportunityEnabled,appState?.sellSheetProductDetails, appState?.territory, appState?.currentProduct?.clientProductId, setAppState]);

	function resetSelectedTarget() {
		setActiveId(null);
		setSearchInput('');
		setShowResults(true);
	}

	// If Territory was skipped (single territory or HCP Opportunity enabled) but Focus was
	// also skipped on entry (batch print disabled), neither page was ever shown, so there's
	// nowhere valid to go back to and the Back button should not be rendered.
	function getBackRoute() {
		if (appState?.bypassTerritorySelection || hcpOpportunityEnabled) {
			return batchPrintEnabled ? '/FocusPage' : null;
		}
		return '/TerritoryPage';
	}

	function handleMarketChange(market) {
		if (!market) {
			return;
		}

		const availableTerritories = market.territories || [];
		const territory = availableTerritories.find((t) => t.id === selectedTerritory?.id) || availableTerritories[0] || null;

		setSelectedMarket(market);
		setTerritoryList(availableTerritories);
		setSelectedTerritory(territory);
		resetSelectedTarget();

		setAppState((prevState) => ({
			...prevState,
			market,
			territory,
			prescriber: null,
			prescriberType: null,
			products: [],
		}));
	}

	function handleTerritoryChange(territory) {
		if (!territory) {
			return;
		}

		setSelectedTerritory(territory);
		resetSelectedTarget();

		setAppState((prevState) => ({
			...prevState,
			territory,
			prescriber: null,
			prescriberType: null,
			products: [],
		}));
	}

	function onProductClick(product) {
		setAppState((prevState) => ({
			...prevState,
			currentProduct: product,
		}));
		setShowProductsModal(false);
	}

	function handleChange(event) {
		setSearchInput(event.target.value);
	}

	const getPrescribers = async () => {
		return !appState?.market?.id || !appState?.territory?.id || (hcpOpportunityEnabled && !appState?.currentProduct?.clientProductId)
			? []
			: await getPrescribersByTerritory(appState.market.id, appState.territory.id, appState.currentProduct?.clientProductId);
	};

	const getGroupPractices = async () => {
		return !appState?.market?.id || !appState?.territory?.id || (hcpOpportunityEnabled && !appState?.currentProduct?.clientProductId)
			? []
			: await getGroupPracticesByTerritory(appState.market.id, appState.territory.id, appState.currentProduct?.clientProductId);
	};

	const getGeographyAggregates = async () => {
		return !appState?.market?.id || !appState?.territory?.id || (hcpOpportunityEnabled && !appState?.currentProduct?.clientProductId)
			? []
			: await getGeographyAggregatesByTerritory(appState.market.id, appState.territory.id, appState.currentProduct?.clientProductId);
	};

	const filter = async function (data, searchInput, hcpFavoritesOnly) {
		if (hcpFavoritesOnly) {
			data = data.filter((row) => row.data.favorite);
		}
		if (searchInput.length > 0) {
			data = filterByValue(data, searchInput.trim(), [], true);
		}
		return data;
	};

	const handleActive = (data, activeTab) => {
		const enabledProducts = appState.sellSheetProductDetails?.map((p) => p.clientProductId);

		if (activeTab === hcpTab.Prescriber) {
			getPrescriberById(data.id, appState?.market?.id, appState?.territory?.id)
				.then(async (data) => {
					await enrichQuickSheetsMessageType(data);
					const prescriber = data;
					const products = prescriber?.products.filter((product) => enabledProducts.includes(product.productId)).map((product) => product) || null;

					setAppState({
						...appState,
						prescriber: prescriber,
						prescriberType: hcpType.Prescriber,
						products: products,
					});
				})
				.catch((error) => {
					showBoundary(error);
				});
			setActiveId(data.id);
		} else if (activeTab === hcpTab.GroupPractice) {
			getGroupPracticeById(data.id, appState?.market?.id, appState?.territory?.id)
				.then(async (data) => {
					await enrichQuickSheetsMessageType(data);
					const prescriber = data;
					const products = prescriber?.products.filter((product) => enabledProducts.includes(product.productId)).map((product) => product) || null;
					setAppState({
						...appState,
						prescriber: prescriber,
						prescriberType: hcpType.GroupPractice,
						products: products,
					});
				})
				.catch((error) => {
					showBoundary(error);
				});
			setActiveId(data.id);
		} else if (activeTab === hcpTab.GeographyAggregate) {
			getGeographyAggregateById(data.id, appState?.market?.id, appState?.territory?.id)
				.then(async (data) => {
					await enrichQuickSheetsMessageType(data);
					const prescriber = data;
					const products = prescriber?.products.filter((product) => enabledProducts.includes(product.productId)).map((product) => product) || null;
					setAppState({
						...appState,
						prescriber: prescriber,
						prescriberType: hcpType.GeographyAggregate,
						products: products,
					});
				})
				.catch((error) => {
					showBoundary(error);
				});
			setActiveId(data.id);
		}
	};

	async function enrichQuickSheetsMessageType(data) {
		if(getConfigValue(appState.config?.QuickSheets?.MessageType?.Enabled, appState)?.Value === 'true') {
			const enrichmentPromises = data.products?.map(async (product) => {
				// determine if the brand and territory has any quicksheets or if there are national quicksheets
				const hasQuickSheets = await getQuickSheets(product.productId, true, false, appState?.territory?.id, data.id)
				.then((data) => {
					return data.length > 0;
				})
				.catch((error) => {
					showBoundary(error);
					return false;
				});

				if (hasQuickSheets){
					// add in the quicksheets message type
					product.messages?.push({
						category: getConfigValue(appState.config?.NavBar?.Menu?.DownloadQuickSheet?.Label, appState)?.Value,
						categoryDisplayOrder: 99,
						isContainerMessage: false,
						message: getConfigValue(appState.config?.QuickSheets?.MessageType?.Description, appState)?.Value,
						messagecode: 'QUICKSHEETS',
						messageTypeId: quickSheetTypeMessageId
					});
				}
			}) || [];

			await Promise.all(enrichmentPromises);
		}
	}	

	return (
		<div>
			<CreateSalesAidStepProgressBar currentStepId={SalesAidStepIds.SELECTTARGET} focusType={appState?.focus} />

			<div className='container-xl mt-5'>
				<div className='row justify-content-center mb-6'>
					<div className='col-12 col-md-12'>
						<h1 className='type-20 color-grey-d1'>{getConfigValue(appState.config?.Target?.SelectTarget, appState)?.Value}</h1>
						{(hcpOpportunityEnabled || topButtonsEnabled) && (
							<div className='row align-items-center mb-5 g-2'>
								{hcpOpportunityEnabled && (
									<div className={topButtonsEnabled ? 'col-12 col-xxl-8' : 'col-12'}>
									<div className='row g-2'>
										<div className='col-12 col-md-4 col-lg-4'>
											<label htmlFor='target-market' className='form-label color-grey-d1 mb-1' style={{ fontSize: selectorFontSize }}>
												{selectMarketConfigValue}
											</label>
											<Select
												unstyled
												inputId='target-market'
												components={{
													IndicatorSeparator: removeIndicatorSeparator,
												}}
												menuPortalTarget={document.body}
												menuPosition='fixed'
												styles={{
													menuPortal: (base) => ({ ...base, zIndex: 9999 }),
													singleValue: (base) => ({ ...base, fontSize: selectorFontSize }),
													placeholder: (base) => ({ ...base, fontSize: selectorFontSize }),
													option: (base) => ({ ...base, fontSize: selectorFontSize }),
												}}
												classNames={{
													control: () => 'form-control',
													menuList: () => 'card',
													option: (state) => (state.isFocused ? 'bg-color-precision-grey text-white p-2' : 'p-2'),
												}}
												aria-label={selectMarketConfigValue}
												placeholder='Select'
												onChange={handleMarketChange}
												options={marketList}
												getOptionLabel={(market) => market.name}
												getOptionValue={(market) => market.id}
												value={selectedMarket}
											></Select>
										</div>
										<div className='col-12 col-md-5 col-lg-5'>
											<label htmlFor='target-territory' className='form-label color-grey-d1 mb-1' style={{ fontSize: selectorFontSize }}>
												{selectTerritoryConfigValue}
											</label>
											<Select
												unstyled
												inputId='target-territory'
												components={{
													IndicatorSeparator: removeIndicatorSeparator,
												}}
												menuPortalTarget={document.body}
												menuPosition='fixed'
												styles={{
													menuPortal: (base) => ({ ...base, zIndex: 9999 }),
													singleValue: (base) => ({ ...base, fontSize: selectorFontSize }),
													placeholder: (base) => ({ ...base, fontSize: selectorFontSize }),
													option: (base) => ({ ...base, fontSize: selectorFontSize }),
												}}
												classNames={{
													control: () => 'form-control',
													menuList: () => 'card',
													option: (state) => (state.isFocused ? 'bg-color-precision-grey text-white p-2' : 'p-2'),
												}}
												aria-label={selectTerritoryConfigValue}
												placeholder='Select'
												onChange={handleTerritoryChange}
												options={territoryList}
												getOptionLabel={(territory) => territory.name}
												getOptionValue={(territory) => territory.id}
												value={selectedTerritory}
											></Select>
										</div>
										<div className={`col-12 col-md-3 col-lg-3 ${topButtonsEnabled ? 'col-xxl-2' : ''}`}>
											<label className='form-label color-grey-d1 mb-1' style={{ fontSize: selectorFontSize }}>
												{productLabel}
											</label>
											<button
												type='button'
												onClick={() => setShowProductsModal(true)}
												className='form-control d-flex justify-content-between align-items-center text-start'
												style={{
													fontSize: selectorFontSize,
													minHeight: '38px',
													padding: '0.375rem 0.75rem'
												}}
											>
												<span className='d-flex align-items-center text-truncate'>
													<span className='fa-solid color-primary me-2'></span>
													<span className='text-truncate'>{currentProductName}</span>
												</span>
												<span className='ms-2 text-nowrap'>
													<span className='fa-solid color-grey-d1'></span>
												</span>
											</button>
										</div>
									</div>
									</div>
								)}
								{topButtonsEnabled && (
								<div className={`col-12 col-xxl-4 mt-2 mt-xxl-0 ${hcpOpportunityEnabled ? 'ps-xl-3' : 'ms-auto'}`}>
									<div className='d-flex flex-column flex-md-row-reverse'>
										{(!activeId || !showResults) && (
											<Link to='#' className='btn btn-primary disabled btn-lg pe-7 mb-3 mb-md-0 ms-md-3 flex-fill w-100'>
												{getConfigValue(appState.config?.Common?.Continue, appState)?.Value}
											</Link>
										)}
										{activeId && showResults && (
											<Link to='/MessageSelectionPage' className='btn btn-primary btn-lg pe-7 mb-3 mb-md-0 ms-md-3 flex-fill w-100'>
												{getConfigValue(appState.config?.Common?.Continue, appState)?.Value}
											</Link>
										)}
										{getBackRoute() && (
											<Link
												to={getBackRoute()}
												className='btn btn-secondary btn-lg pe-7'
											>
												{getConfigValue(appState.config?.Common?.Back, appState)?.Value}
											</Link>
										)}
									</div>
								</div>
								)}
							</div>
						)}

						<SelectTargetComponent
							key={`${appState?.market?.id || 'no-market'}-${appState?.territory?.id || 'no-territory'}-${appState?.currentProduct?.clientProductId || 'no-product'}`}
							filterFunction={filter}
							handleChange={handleChange}
							activeId={activeId}
							setShowResults={setShowResults}
							showResults={showResults}
							getPrescribers={getPrescribers}
							getGroupPractices={getGroupPractices}
							getGeographyAggregates={getGeographyAggregates}
							handleActive={handleActive}
							searchInput={searchInput}
							setSearchInput={setSearchInput}
							useHcpFiltersDropdown={true}
						/>
						{hcpOpportunityEnabled && (
							<ProductsModal show={showProductsModal} setShow={setShowProductsModal} onProductClick={onProductClick} onlySellSheetProducts={true} />
						)}
					</div>
				</div>
            </div>

			<div className={`${useStickyNavigation ? 'button-bottom-container-sticky-low-z' : ''}`}>
				<div className='container-xl'>
					<div className='row justify-content-center justify-content-lg-between py-3'>
						<div className='col-10 col-sm-8 col-md-6 col-xl-5'>
							<div className='d-flex flex-column flex-lg-row'>
								<div className='flex-fill w-100 ms-lg-3 pe-3'></div>
							</div>
						</div>
						<div className='w-100 d-lg-none'></div>
						<div className='col-12'>
							<div className="d-flex flex-column flex-md-row-reverse" style={{ float: "right" }}>
								{(!activeId || !showResults) && (
									<Link to='#' className='btn btn-primary disabled btn-lg pe-7 flex-fill w-100 ml-1'>
										{getConfigValue(appState.config?.Common?.Continue, appState)?.Value}
									</Link>
								)}
								{activeId && showResults && (
									<Link to='/MessageSelectionPage' className='btn btn-primary btn-lg pe-7 flex-fill w-100 ml-1'>
										{getConfigValue(appState.config?.Common?.Continue, appState)?.Value}
									</Link>
								)}
								{getBackRoute() && (
									<Link to={getBackRoute()} className='btn btn-secondary btn-lg pe-7 flex-fill w-100'>
										{getConfigValue(appState.config?.Common?.Back, appState)?.Value}
									</Link>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default HCPPage;
